import { z } from "zod";

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const nullableIsoDateSchema = isoDateSchema.nullable();
const nullableString = (maxLength: number) => z.string().trim().min(1).max(maxLength).nullable();
const stringListSchema = (maxItems: number, maxLength: number) =>
  z.array(z.string().trim().min(1).max(maxLength)).max(maxItems);
const sortOrderSchema = z.number().int().min(0);

export const specialistPortfolioProjectSchema = z.object({
  id: z.string().uuid(),
  specialistProfileId: z.string().uuid(),
  projectName: z.string(),
  projectRole: z.string(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  isCurrent: z.boolean(),
  industry: z.string().nullable(),
  tasks: z.string().nullable(),
  result: z.string().nullable(),
  stack: z.array(z.string()),
  links: z.array(z.string()),
  isNda: z.boolean(),
  sortOrder: z.number().int(),
  createdByUserId: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const specialistPortfolioProjectCreateRequestSchema = z
  .object({
    projectName: z.string().trim().min(1).max(180),
    projectRole: z.string().trim().min(1).max(160),
    startDate: isoDateSchema.nullable().optional(),
    endDate: nullableIsoDateSchema.optional(),
    isCurrent: z.boolean().default(false),
    industry: nullableString(160).optional(),
    tasks: nullableString(4_000).optional(),
    result: nullableString(4_000).optional(),
    stack: stringListSchema(50, 80).default([]),
    links: stringListSchema(20, 300).default([]),
    isNda: z.boolean().default(false),
    sortOrder: sortOrderSchema.default(0),
  })
  .strict();

export const specialistPortfolioProjectUpdateRequestSchema = z
  .object({
    projectName: z.string().trim().min(1).max(180).optional(),
    projectRole: z.string().trim().min(1).max(160).optional(),
    startDate: isoDateSchema.nullable().optional(),
    endDate: nullableIsoDateSchema.optional(),
    isCurrent: z.boolean().optional(),
    industry: nullableString(160).optional(),
    tasks: nullableString(4_000).optional(),
    result: nullableString(4_000).optional(),
    stack: stringListSchema(50, 80).optional(),
    links: stringListSchema(20, 300).optional(),
    isNda: z.boolean().optional(),
    sortOrder: sortOrderSchema.optional(),
  })
  .strict();

export const specialistPortfolioProjectListResponseSchema = z.object({
  items: z.array(specialistPortfolioProjectSchema),
  total: z.number().int().nonnegative(),
});

export type SpecialistPortfolioProject = z.infer<typeof specialistPortfolioProjectSchema>;
export type SpecialistPortfolioProjectCreateRequest = z.infer<
  typeof specialistPortfolioProjectCreateRequestSchema
>;
export type SpecialistPortfolioProjectUpdateRequest = z.infer<
  typeof specialistPortfolioProjectUpdateRequestSchema
>;
export type SpecialistPortfolioProjectListResponse = z.infer<
  typeof specialistPortfolioProjectListResponseSchema
>;
